/**
 * SynopsisPage — Research Synopsis / Proposal generator.
 *
 * Flow:
 *   1. Check for existing SYNOPSIS document
 *   2. If found → offer to continue or generate new
 *   3. Setup form → AI suggest title modal → Generate → stream
 */

import { useState, useEffect, useRef } from 'react'
import { marked } from 'marked'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../lib/supabase'
import { streamAnthropic, generateAnthropic } from '../../lib/anthropic'
import { useAIStore } from '../../store/aiStore'
import { buildSynopsisPrompt } from '../../prompts/synopsis'
import DocumentEditor from '../../components/documents/DocumentEditor'
import { Sparkles, ArrowLeft, ChevronRight, Loader2, Wand2, X } from 'lucide-react'
import toast from 'react-hot-toast'

// ─── Shared primitives ────────────────────────────────────────────────────────

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block font-sans text-sm font-medium text-acdyon-textMuted mb-1.5">
        {label}
        {required && <span style={{ color: '#2563eb' }}> *</span>}
      </label>
      {hint && <p className="font-sans text-xs text-acdyon-textFaint mb-2">{hint}</p>}
      {children}
    </div>
  )
}

const inputCls   = "w-full px-3.5 py-2.5 rounded-lg font-sans text-sm text-acdyon-text placeholder:text-acdyon-textFaint outline-none transition-colors"
const inputStyle = { background: '#FFFFFF', border: '1px solid #C8D4E4' }
const inputFocus = {
  onFocus: e => (e.currentTarget.style.borderColor = '#2563eb'),
  onBlur:  e => (e.currentTarget.style.borderColor = '#C8D4E4'),
}

// ─── Methodology options ──────────────────────────────────────────────────────

const METHODOLOGIES = [
  { id: 'Mixed Methods',      label: 'Mixed Methods',     desc: 'Quantitative + qualitative' },
  { id: 'Quantitative',       label: 'Quantitative',      desc: 'Surveys, experiments, stats' },
  { id: 'Qualitative',        label: 'Qualitative',       desc: 'Interviews, thematic analysis' },
  { id: 'Systematic Review',  label: 'Systematic Review', desc: 'Literature synthesis' },
  { id: 'Case Study',         label: 'Case Study',        desc: 'In-depth contextual study' },
  { id: 'Action Research',    label: 'Action Research',   desc: 'Participatory, iterative' },
]

function MethodRadio({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {METHODOLOGIES.map(m => (
        <button
          key={m.id}
          type="button"
          onClick={() => onChange(m.id)}
          className="text-left px-3 py-2.5 rounded-lg transition-all"
          style={{
            background: value === m.id ? 'rgba(37,99,235,0.08)' : '#F4F7FC',
            border: `1px solid ${value === m.id ? 'rgba(37,99,235,0.4)' : '#DDE4EF'}`,
          }}
        >
          <p className="font-sans text-xs font-semibold" style={{ color: value === m.id ? '#2563eb' : '#0C1629' }}>{m.label}</p>
          <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">{m.desc}</p>
        </button>
      ))}
    </div>
  )
}

// ─── Objectives slider ────────────────────────────────────────────────────────

function ObjectivesSlider({ value, onChange }) {
  return (
    <div className="flex items-center gap-4">
      <input
        type="range" min={2} max={6} step={1}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="flex-1 accent-brand-600"
      />
      <span className="font-mono text-sm font-semibold text-brand-600 w-6 text-right">{value}</span>
    </div>
  )
}

// ─── AI title suggest modal ───────────────────────────────────────────────────

function TitleSuggestModal({ researchTopic, onSelect, onClose }) {
  const [loading,     setLoading]     = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [error,       setError]       = useState('')

  const fetch = async () => {
    setLoading(true)
    setError('')
    try {
      const text = await generateAnthropic({
        feature:   'abstract',
        maxTokens: 400,
        messages:  [{ role: 'user', content: `Generate 5 strong, specific doctoral research thesis titles for the topic: "${researchTopic}". Return ONLY a JSON array of strings, no other text. Example: ["Title 1","Title 2","Title 3","Title 4","Title 5"]` }],
      })
      const cleaned = text.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim()
      const titles  = JSON.parse(cleaned)
      setSuggestions(Array.isArray(titles) ? titles : [])
    } catch {
      setError('Could not generate titles. Try typing one manually.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetch() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(6,11,20,0.85)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-6 space-y-4"
        style={{ background: '#FFFFFF', border: '1px solid #DDE4EF' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <p className="font-sans font-semibold text-acdyon-text text-sm">AI Title Suggestions</p>
          <button onClick={onClose}><X style={{ width: 16, height: 16, color: '#8EA4BC' }} /></button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 py-4 justify-center">
            <Loader2 style={{ width: 16, height: 16, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
            <span className="font-sans text-sm text-acdyon-textFaint">Generating suggestions…</span>
          </div>
        )}

        {error && <p className="font-sans text-xs" style={{ color: '#E8544A' }}>{error}</p>}

        <div className="space-y-2">
          {suggestions.map((t, i) => (
            <button
              key={i}
              onClick={() => onSelect(t)}
              className="w-full text-left px-4 py-3 rounded-xl font-sans text-sm text-acdyon-text transition-colors"
              style={{ background: '#FFFFFF', border: '1px solid #C8D4E4' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(37,99,235,0.3)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = '#DDE4EF')}
            >
              {t}
            </button>
          ))}
        </div>

        {!loading && (
          <button onClick={fetch} className="font-sans text-xs text-acdyon-textFaint underline hover:text-acdyon-textMuted transition-colors">
            Regenerate suggestions
          </button>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── Existing doc card ────────────────────────────────────────────────────────

function ExistingCard({ doc, onEdit, onNew }) {
  return (
    <div className="rounded-2xl p-6 space-y-4" style={{ background: '#FFFFFF', border: '1px solid #DDE4EF', maxWidth: 520 }}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)' }}>
          <Sparkles style={{ width: 18, height: 18, color: '#2563eb' }} />
        </div>
        <div>
          <p className="font-sans font-semibold text-acdyon-text text-sm">{doc.title}</p>
          <p className="font-mono text-xs text-acdyon-textFaint mt-0.5">
            {(doc.word_count || 0).toLocaleString()} words · {new Date(doc.updated_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={onEdit} className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
          style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.3)', color: '#2563eb' }}>
          Continue Editing
        </button>
        <button onClick={onNew} className="flex-1 py-2.5 rounded-xl font-sans text-sm font-medium transition-all"
          style={{ background: '#FFFFFF', border: '1px solid #C8D4E4', color: '#506280' }}>
          Generate New
        </button>
      </div>
    </div>
  )
}

// ─── Setup Form ───────────────────────────────────────────────────────────────

function SetupForm({ profile, onGenerate }) {
  const [form, setForm] = useState({
    title:                 '',
    researchTopic:         profile?.research_topic || '',
    domain:                profile?.domain         || '',
    university:            profile?.university     || '',
    programme:             profile?.programme      || '',
    methodology:           'Mixed Methods',
    numObjectives:         4,
    theoreticalFrameworks: '',
    keywords:              profile?.keywords?.join(', ') || '',
    targetWords:           3400,
  })

  const [showTitleModal, setShowTitleModal] = useState(false)
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }))

  const canSubmit = form.researchTopic && form.university && form.programme

  return (
    <>
      <form onSubmit={e => { e.preventDefault(); if (canSubmit) onGenerate(form) }} className="space-y-6">

        {/* Title */}
        <Field label="Proposed Title" hint="Leave blank for AI to create one, or use AI suggest for options.">
          <div className="flex gap-2">
            <input
              className={inputCls + ' flex-1'} style={inputStyle} {...inputFocus}
              value={form.title}
              onChange={set('title')}
              placeholder="A Study of…"
            />
            <button
              type="button"
              onClick={() => setShowTitleModal(true)}
              disabled={!form.researchTopic}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg font-sans text-xs font-medium transition-all disabled:opacity-40 shrink-0"
              style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.3)', color: '#2563eb' }}
            >
              <Wand2 style={{ width: 13, height: 13 }} />
              AI Suggest
            </button>
          </div>
        </Field>

        {/* Research Topic */}
        <Field label="Research Topic" required>
          <textarea
            className={inputCls} style={inputStyle} {...inputFocus}
            rows={3}
            value={form.researchTopic}
            onChange={set('researchTopic')}
            placeholder="e.g. The impact of AI tutoring systems on student self-efficacy in higher education"
          />
        </Field>

        {/* Row: Domain + University + Programme */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Domain / Discipline">
            <input
              className={inputCls} style={inputStyle} {...inputFocus}
              value={form.domain}
              onChange={set('domain')}
              placeholder="e.g. Education Technology"
            />
          </Field>
          <Field label="Target University" required>
            <input
              className={inputCls} style={inputStyle} {...inputFocus}
              value={form.university}
              onChange={set('university')}
              placeholder="e.g. University of Edinburgh"
            />
          </Field>
        </div>

        <Field label="Programme" required>
          <input
            className={inputCls} style={inputStyle} {...inputFocus}
            value={form.programme}
            onChange={set('programme')}
            placeholder="e.g. Doctorate in Education"
          />
        </Field>

        {/* Methodology */}
        <Field label="Methodology">
          <MethodRadio value={form.methodology} onChange={v => setForm(prev => ({ ...prev, methodology: v }))} />
        </Field>

        {/* Objectives count */}
        <Field label={`Number of Research Objectives: ${form.numObjectives}`}>
          <ObjectivesSlider value={form.numObjectives} onChange={v => setForm(prev => ({ ...prev, numObjectives: v }))} />
        </Field>

        {/* Theoretical frameworks */}
        <Field label="Theoretical Frameworks" hint="Optional — comma-separated (e.g. Social Cognitive Theory, Constructivism)">
          <textarea
            className={inputCls} style={inputStyle} {...inputFocus}
            rows={2}
            value={form.theoreticalFrameworks}
            onChange={set('theoreticalFrameworks')}
            placeholder="Vygotsky's Zone of Proximal Development, Self-Determination Theory…"
          />
        </Field>

        {/* Keywords */}
        <Field label="Keywords" hint="Comma-separated — used to surface relevant literature in the synopsis.">
          <input
            className={inputCls} style={inputStyle} {...inputFocus}
            value={form.keywords}
            onChange={set('keywords')}
            placeholder="AI tutoring, self-efficacy, higher education, formative assessment"
          />
        </Field>

        {/* Target words */}
        <Field label="Target Length (words)">
          <div className="flex items-center gap-4">
            <input
              type="range" min={1500} max={5000} step={100}
              value={form.targetWords}
              onChange={e => setForm(prev => ({ ...prev, targetWords: Number(e.target.value) }))}
              className="flex-1 accent-brand-600"
            />
            <span className="font-mono text-sm font-semibold text-brand-600 w-16 text-right">
              {form.targetWords.toLocaleString()}
            </span>
          </div>
        </Field>

        <button
          type="submit"
          disabled={!canSubmit}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-sans text-sm font-semibold transition-all disabled:opacity-50"
          style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.4)', color: '#2563eb' }}
        >
          <Sparkles style={{ width: 15, height: 15 }} />
          Generate Synopsis
          <ChevronRight style={{ width: 15, height: 15 }} />
        </button>
      </form>

      {showTitleModal && (
        <TitleSuggestModal
          researchTopic={form.researchTopic}
          onSelect={title => { setForm(prev => ({ ...prev, title })); setShowTitleModal(false) }}
          onClose={() => setShowTitleModal(false)}
        />
      )}
    </>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SynopsisPage() {
  const { profile } = useAuthStore()

  const [phase,       setPhase]       = useState('loading')
  const [existingDoc, setExistingDoc] = useState(null)
  const [documentId,  setDocumentId]  = useState(null)
  const [docTitle,    setDocTitle]    = useState('')
  const [docContent,  setDocContent]  = useState('')
  const [streaming,   setStreaming]   = useState(false)
  const [formData,    setFormData]    = useState(null)

  const editorRef = useRef(null)
  const abortRef  = useRef(false)
  const aiStore   = useAIStore()

  useEffect(() => {
    const check = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) { setPhase('form'); return }
        const { data } = await supabase
          .from('documents')
          .select('id, type, title, word_count, updated_at, content')
          .eq('user_id', user.id)
          .eq('type', 'SYNOPSIS')
          .order('updated_at', { ascending: false })
          .limit(1)
        const syn = data?.[0]
        if (syn) { setExistingDoc(syn); setPhase('check') }
        else       setPhase('form')
      } catch { setPhase('form') }
    }
    check()
  }, [])

  const openExisting = () => {
    setDocumentId(existingDoc.id)
    setDocTitle(existingDoc.title)
    setDocContent(existingDoc.content || '')
    setPhase('editor')
  }

  const handleGenerate = async (params) => {
    try {
      aiStore.assertEnabled()
    } catch (err) {
      toast.error(err.message)
      return
    }

    setFormData(params)
    setPhase('editor')
    setStreaming(true)
    abortRef.current = false
    await new Promise(r => setTimeout(r, 120))
    editorRef.current?.clearContent()

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const prompt = buildSynopsisPrompt(params)
      let fullMarkdown = ''

      await streamAnthropic({
        feature:   'synopsis',
        maxTokens: 8000,
        messages:  [{ role: 'user', content: prompt }],
        onChunk: (text) => {
          if (abortRef.current) return
          fullMarkdown += text
        },
      })

      if (abortRef.current) return

      aiStore.increment()

      const title = params.title || 'Research Synopsis'
      const wordCount = fullMarkdown.trim().split(/\s+/).length

      const html = await marked.parse(fullMarkdown, { async: false })
      editorRef.current?.setHTML(html)

      const { data: saved } = await supabase.from('documents').insert({
        user_id:    user.id,
        type:       'SYNOPSIS',
        title,
        content:    html,
        word_count: wordCount,
        status:     'DRAFT',
      }).select('id').single()

      if (saved?.id) {
        setDocumentId(saved.id)
        setDocTitle(title)
      }
    } catch (err) {
      console.error('Synopsis generation error:', err)
      toast.error(err.message || 'Generation failed')
    } finally {
      setStreaming(false)
    }
  }

  const stopStreaming = () => { abortRef.current = true; setStreaming(false) }

  if (phase === 'loading') {
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 4rem)' }}>
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (phase === 'editor') {
    return (
      <DocumentEditor
        ref={editorRef}
        documentId={documentId}
        initialContent={docContent}
        placeholder="Your Research Synopsis begins here…"
        targetWords={formData?.targetWords || 2500}
        docTitle={docTitle}
        streaming={streaming}
        streamingLabel="Writing your Research Synopsis…"
        onStopStreaming={streaming ? stopStreaming : null}
        onSaved={(id) => setDocumentId(id)}
      />
    )
  }

  return (
    <div className="min-h-screen p-8" style={{ background: '#F4F7FC' }}>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)' }}>
              <Sparkles style={{ width: 18, height: 18, color: '#2563eb' }} />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-acdyon-text">Research Synopsis</h1>
              <p className="font-sans text-sm text-acdyon-textFaint">10-section proposal with SMART objectives and literature</p>
            </div>
          </div>
        </div>

        {phase === 'check' && existingDoc && (
          <div className="mb-8">
            <p className="font-sans text-sm text-acdyon-textMuted mb-4">
              You have an existing synopsis. Continue editing or generate a new one?
            </p>
            <ExistingCard doc={existingDoc} onEdit={openExisting} onNew={() => setPhase('form')} />
          </div>
        )}

        {phase === 'form' && (
          <div className="rounded-2xl p-6" style={{ background: '#FFFFFF', border: '1px solid #DDE4EF' }}>
            {existingDoc && (
              <button
                onClick={() => setPhase('check')}
                className="flex items-center gap-1.5 mb-5 font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              >
                <ArrowLeft style={{ width: 14, height: 14 }} />
                Back
              </button>
            )}
            <h2 className="font-sans text-sm font-semibold text-acdyon-text mb-1">Your Research Proposal</h2>
            <p className="font-sans text-xs text-acdyon-textFaint mb-6">
              Pre-filled from your profile where available — adjust as needed.
            </p>
            <SetupForm profile={profile} onGenerate={handleGenerate} />
          </div>
        )}
      </div>
    </div>
  )
}
